import { ChevronRight } from 'lucide-react';
import { createContext, useContext, useId, useState, useSyncExternalStore } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import {
  DropdownMenuContent as ContentPrimitive,
  DropdownMenuGroup,
  DropdownMenuItem as ItemPrimitive,
  DropdownMenuLabel as LabelPrimitive,
  DropdownMenuSeparator as SeparatorPrimitive,
  DropdownMenuSub as SubPrimitive,
  DropdownMenuSubContent as SubContentPrimitive,
  DropdownMenuSubTrigger as SubTriggerPrimitive,
} from '../primitives/dropdown-menu.tsx';
import { Kbd } from './Kbd.tsx';

/**
 * Menus of actions, on the shadcn DropdownMenu (components.md, Overlays): items are verb plus
 * object, items that open more UI end in "…", the destructive item goes last after a
 * separator. The parts without extra props are the primitives themselves.
 */
export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu.tsx';

/** Opens below the trigger, aligned to its end. */
export function DropdownMenuContent({ sideOffset = 6, align = 'end', collisionPadding = 8, ...rest }: ComponentProps<typeof ContentPrimitive>) {
  return <ContentPrimitive sideOffset={sideOffset} align={align} collisionPadding={collisionPadding} {...rest} />;
}

export interface DropdownMenuItemProps extends ComponentProps<typeof ItemPrimitive> {
  icon?: ReactNode;
  shortcut?: string[];
  /** The destructive item: danger text, last in the menu after a separator. */
  danger?: boolean;
}

export function DropdownMenuItem({ icon, shortcut, danger, variant, className, children, ...rest }: DropdownMenuItemProps) {
  return (
    <ItemPrimitive variant={danger ? 'destructive' : variant} className={className} {...rest}>
      {icon && <span className="flex [&_svg]:size-4">{icon}</span>}
      <span className="flex-1 truncate">{children}</span>
      {shortcut && <Kbd keys={shortcut} />}
    </ItemPrimitive>
  );
}

/** A group label inside a menu: 12 px, fg-subtle. */
export function DropdownMenuLabel({ className, ...rest }: ComponentProps<typeof LabelPrimitive>) {
  return <LabelPrimitive className={cn(className)} {...rest} />;
}

/**
 * The line between groups (before the destructive item). A wash of the text colour, so it shows
 * on the raised menu surface in dark as it does in light (the subtle border token matches it).
 */
export function DropdownMenuSeparator({ className, ...rest }: ComponentProps<typeof SeparatorPrimitive>) {
  return <SeparatorPrimitive className={cn('bg-fg/10', className)} {...rest} />;
}

/** Phones: below 768 px (the phone breakpoint) a side flyout has no room, so submenus open in place. */
const PHONE_QUERY = '(max-width: 47.99em)';

function subscribePhone(onChange: () => void): () => void {
  if (typeof matchMedia !== 'function') return () => {};
  const mq = matchMedia(PHONE_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function isPhone(): boolean {
  return typeof matchMedia === 'function' && matchMedia(PHONE_QUERY).matches;
}

function usePhone(): boolean {
  return useSyncExternalStore(subscribePhone, isPhone, () => false);
}

interface InlineSub {
  open: boolean;
  toggle: () => void;
  id: string;
}

const InlineSubContext = createContext<InlineSub | null>(null);

/**
 * A submenu. On a desktop it is a flyout beside its trigger. On a phone it opens in place, inside
 * the same menu: the trigger becomes a disclosure and its choices appear indented under it, so a
 * flyout never runs off the screen or covers the page.
 */
export function DropdownMenuSub({ children, open, defaultOpen, onOpenChange, ...rest }: ComponentProps<typeof SubPrimitive>) {
  const phone = usePhone();
  const [own, setOwn] = useState(defaultOpen ?? false);
  const id = useId();
  if (!phone) {
    return (
      <SubPrimitive open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} {...rest}>
        {children}
      </SubPrimitive>
    );
  }
  const shown = open ?? own;
  const toggle = () => {
    setOwn(!shown);
    onOpenChange?.(!shown);
  };
  return (
    <InlineSubContext.Provider value={{ open: shown, toggle, id }}>
      <DropdownMenuGroup>{children}</DropdownMenuGroup>
    </InlineSubContext.Provider>
  );
}

export function DropdownMenuSubTrigger({ className, inset, children, ...rest }: ComponentProps<typeof SubTriggerPrimitive>) {
  const inline = useContext(InlineSubContext);
  if (!inline) {
    return (
      <SubTriggerPrimitive className={className} inset={inset} {...rest}>
        {children}
      </SubTriggerPrimitive>
    );
  }
  return (
    <ItemPrimitive
      inset={inset}
      aria-expanded={inline.open}
      aria-controls={inline.id}
      className={cn(inline.open && 'bg-fg/6', className)}
      onSelect={(e) => {
        // Opening the choices keeps the menu open.
        e.preventDefault();
        inline.toggle();
      }}
      {...(rest as ComponentProps<typeof ItemPrimitive>)}
    >
      {children}
      <ChevronRight aria-hidden className={cn('ml-auto size-4 transition-transform duration-(--duration-fast)', inline.open && 'rotate-90')} />
    </ItemPrimitive>
  );
}

export function DropdownMenuSubContent({ className, children, ...rest }: ComponentProps<typeof SubContentPrimitive>) {
  const inline = useContext(InlineSubContext);
  if (!inline) {
    return (
      <SubContentPrimitive collisionPadding={8} className={className} {...rest}>
        {children}
      </SubContentPrimitive>
    );
  }
  if (!inline.open) return null;
  return (
    <div id={inline.id} role="group" className="ml-3 border-l border-fg/10 pl-1">
      {children}
    </div>
  );
}
